import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import api from '../lib/api';
import { formatCurrency, formatDateTime, formatDate, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Mail, 
  XCircle, 
  CheckCircle,
  User,
  CreditCard,
  MapPin,
  Phone,
  Calendar,
  FileText,
  Send,
  Loader2,
  Copy,
  Download,
  Pencil
} from 'lucide-react';

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [csvDownloaded, setCsvDownloaded] = useState(false);
  const [contractDownloaded, setContractDownloaded] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const response = await api.getOrder(orderId);
      setOrder(response.data);
    } catch (error) {
      toast.error('Bestellung nicht gefunden');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleSendEmail = async () => {
    setSending(true);
    try {
      await api.sendOrderEmail(orderId);
      toast.success('E-Mail erfolgreich gesendet');
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.cancelOrder(orderId);
      toast.success('Bestellung storniert');
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stornierung fehlgeschlagen');
    } finally {
      setCancelling(false);
    }
  };

  const triggerDownload = (blob, filename) => {
    // Create object URL from the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Append to body and trigger download
    document.body.appendChild(link);
    
    // Use setTimeout to ensure the link is in DOM before clicking
    setTimeout(() => {
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    }, 0);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    setPdfDownloaded(false);
    try {
      const response = await api.downloadVorvertraglicheInfo(orderId);
      
      // Ensure we have data
      if (!response.data || response.data.size === 0) {
        throw new Error('Leere Antwort vom Server');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `Vorvertragliche-Informationen-${order.order_number}.pdf`;
      
      triggerDownload(blob, filename);
      
      setPdfDownloaded(true);
      toast.success('PDF erfolgreich heruntergeladen');
    } catch (error) {
      setPdfDownloaded(false);
      toast.error(error.response?.data?.detail || 'PDF konnte nicht heruntergeladen werden');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    setCsvDownloaded(false);
    try {
      const response = await api.downloadOrderCsv(orderId);
      
      // Ensure we have data
      if (!response.data || response.data.size === 0) {
        throw new Error('Leere Antwort vom Server');
      }
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const filename = `Bestellung-${order.order_number}.csv`;
      
      triggerDownload(blob, filename);
      
      setCsvDownloaded(true);
      toast.success('CSV erfolgreich heruntergeladen');
    } catch (error) {
      setCsvDownloaded(false);
      toast.error(error.response?.data?.detail || 'CSV konnte nicht heruntergeladen werden');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleDownloadContract = async () => {
    setDownloadingContract(true);
    setContractDownloaded(false);
    try {
      const response = await api.downloadContractPdf(orderId);
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Leere Antwort vom Server');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `Vertrag-${order.order_number}.pdf`;
      
      triggerDownload(blob, filename);
      
      setContractDownloaded(true);
      toast.success('Vertrag erfolgreich heruntergeladen');
    } catch (error) {
      setContractDownloaded(false);
      
      // Try to get error message from blob response
      let errorMessage = 'Vertrag konnte nicht heruntergeladen werden';
      if (error.response?.data) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.detail || errorMessage;
        } catch {
          errorMessage = error.response?.data?.detail || error.message || errorMessage;
        }
      }
      toast.error(errorMessage);
    } finally {
      setDownloadingContract(false);
    }
  };

  const copyConfirmationLink = () => {
    const link = `${window.location.origin}/confirm/${order.confirmation_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link kopiert');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  if (!order) return null;

  const customer = order.customer;
  const sepa = order.sepa;

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/orders')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bestellung #{order.order_number}
              </h1>
              <Badge className={STATUS_COLORS[order.status]}>
                {STATUS_LABELS[order.status]}
              </Badge>
            </div>
            <p className="text-slate-500 mt-1">
              Erstellt am {formatDateTime(order.created_at)} von {order.sales_user_name}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      {order.status !== 'cancelled' && order.status !== 'confirmed' && (
        <Card className="border-0 shadow-sm bg-slate-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <p className="font-medium text-slate-900">
                  {order.status === 'pending' 
                    ? 'E-Mail noch nicht versendet'
                    : 'Warte auf Kundenbestätigung'}
                </p>
                <p className="text-sm text-slate-500">
                  {order.status === 'pending'
                    ? 'Senden Sie die Kundenportal-Zugangsdaten an den Kunden'
                    : 'Der Kunde muss die Bestellung im Portal bestätigen'}
                </p>
              </div>
              <div className="flex gap-2">
                {order.status === 'pending' && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/orders/${orderId}/edit`)}
                    className="gap-2"
                    data-testid="edit-order-btn"
                  >
                    <Pencil className="w-4 h-4" />
                    Bearbeiten
                  </Button>
                )}
                <Button
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="bg-[#F47920] hover:bg-[#E06810] gap-2"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {order.status === 'sent' ? 'Erneut senden' : 'E-Mail senden'}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2 text-red-600 hover:text-red-700" data-testid="cancel-order-btn">
                      <XCircle className="w-4 h-4" />
                      Stornieren
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Bestellung stornieren?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Möchten Sie die Bestellung #{order.order_number} wirklich stornieren? 
                        Diese Aktion kann nicht rückgängig gemacht werden.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCancel}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={cancelling}
                      >
                        {cancelling ? 'Wird storniert...' : 'Stornieren'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            
            {/* Access Code Display */}
            {order.access_code && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Kundenportal-Zugangsdaten:</p>
                <div className="flex flex-wrap items-center gap-4 bg-white p-3 rounded-lg border">
                  <div>
                    <span className="text-xs text-slate-500">Bestellnummer</span>
                    <p className="font-mono font-bold text-lg">{order.order_number}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Zugangscode</span>
                    <p className="font-mono font-bold text-lg text-[#F47920]">{order.access_code}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`Bestellnummer: ${order.order_number}\nZugangscode: ${order.access_code}\nPortal: ${window.location.origin}/kunde`);
                      toast.success('Zugangsdaten kopiert');
                    }}
                    className="ml-auto"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Kopieren
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmed Badge */}
      {order.status === 'confirmed' && (
        <Card className="border-0 shadow-sm bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-emerald-900">Bestellung bestätigt</p>
                <p className="text-sm text-emerald-700">
                  Am {formatDateTime(order.confirmed_at)} vom Kunden bestätigt
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#F47920]" />
            Vertragsunterlagen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="bg-[#F47920] hover:bg-[#E06810] gap-2"
              data-testid="download-vorvertragliche"
            >
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              Vorvertragliche Informationen (PDF)
              {pdfDownloaded && <CheckCircle className="w-4 h-4 text-emerald-500" />}
            </Button>
            
            {order.status === 'confirmed' && (
              <Button
                onClick={handleDownloadCsv}
                disabled={downloadingCsv}
                variant="outline"
                className="gap-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="download-csv"
              >
                {downloadingCsv ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                CSV für Warenwirtschaft
                {csvDownloaded && <CheckCircle className="w-4 h-4 text-emerald-500" />}
              </Button>
            )}
            
            {order.status === 'confirmed' && (
              <Button
                onClick={handleDownloadContract}
                disabled={downloadingContract}
                variant="outline"
                className="gap-2 border-[#F47920] text-[#F47920] hover:bg-orange-50"
                data-testid="download-contract"
              >
                {downloadingContract ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Vertrag (PDF)
                {contractDownloaded && <CheckCircle className="w-4 h-4 text-emerald-500" />}
              </Button>
            )}
          </div>
          
          {/* Download Status */}
          {(pdfDownloaded || csvDownloaded || contractDownloaded) && (
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">
                  Dateien erfolgreich heruntergeladen
                </span>
              </div>
            </div>
          )}
          
          <p className="text-sm text-slate-500 mt-3">
            {order.status === 'confirmed' 
              ? 'Die CSV-Datei kann direkt in die Warenwirtschaft importiert werden.'
              : 'Das Dokument enthält alle bestellspezifischen Informationen.'}
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Product Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#F47920]" />
              Produkt
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">{order.product_name}</p>
              <p className="text-slate-500">
                {order.contract_type === '24m' ? '24 Monate Laufzeit' : 'Ohne Mindestlaufzeit'}
              </p>
            </div>
            
            {/* Hardware */}
            <div className="pt-4 border-t">
              <p className="font-medium text-slate-700 mb-2">Hardware:</p>
              <p className={order.include_router !== false ? "text-green-600" : "text-amber-600"}>
                {order.include_router !== false ? '2x FRITZ!Box inklusive' : 'Kein Router (eigene Hardware)'}
              </p>
            </div>
            
            {order.addons && order.addons.length > 0 && (
              <div className="pt-4 border-t">
                <p className="font-medium text-slate-700 mb-2">Zusatzoptionen:</p>
                <div className="flex flex-wrap gap-2">
                  {order.addons.map((addon) => (
                    <Badge key={addon} variant="secondary">{addon}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Special agreements */}
            {order.custom_setup_fee !== null && order.custom_setup_fee !== undefined && (
              <div className="pt-4 border-t">
                <p className="font-medium text-amber-700 mb-1">Individuelle Installationskosten:</p>
                <p className={`${order.custom_setup_fee === 0 ? 'text-green-600' : 'text-amber-600'} bg-amber-50 p-2 rounded`}>
                  {order.custom_setup_fee === 0 ? 'Kostenlos (0,00 €)' : formatCurrency(order.custom_setup_fee)}
                </p>
              </div>
            )}
            
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-600">Monatliche Kosten:</span>
                <span className="font-semibold">{formatCurrency(order.monthly_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Einmalige Kosten:</span>
                <span className="font-semibold">
                  {order.setup_fee === 0 ? (
                    <span className="text-green-600">0,00 € (kostenlos)</span>
                  ) : (
                    formatCurrency(order.setup_fee)
                  )}
                </span>
              </div>
            </div>
            
            {/* Notes/Remarks */}
            {order.notes && (
              <div className="pt-4 border-t">
                <p className="font-medium text-amber-700 mb-2">Anmerkungen/Sondervereinbarungen:</p>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="whitespace-pre-wrap text-slate-700">{order.notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#F47920]" />
              Kunde
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">
                {customer.salutation} {customer.first_name} {customer.last_name}
              </p>
              {customer.company_name && (
                <p className="text-slate-500">{customer.company_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${customer.email}`} className="hover:text-[#F47920]">
                  {customer.email}
                </a>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4" />
                <a href={`tel:${customer.phone}`} className="hover:text-[#F47920]">
                  {customer.phone}
                </a>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(customer.birth_date)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Address */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#F47920]" />
              Anschlussadresse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">
              {customer.service_street} {customer.service_house_number}
            </p>
            <p className="text-slate-900">
              {customer.service_postal_code} {customer.service_city}
            </p>
            {!customer.billing_same_as_service && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-slate-500 mb-2">Rechnungsadresse:</p>
                <p className="text-slate-900">
                  {customer.billing_street} {customer.billing_house_number}
                </p>
                <p className="text-slate-900">
                  {customer.billing_postal_code} {customer.billing_city}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SEPA Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#F47920]" />
              Bankverbindung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-slate-500">Kontoinhaber</p>
              <p className="font-medium text-slate-900">{sepa.account_holder}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">IBAN</p>
              <p className="font-medium text-slate-900 font-mono">{sepa.iban}</p>
            </div>
            {sepa.bic && (
              <div>
                <p className="text-sm text-slate-500">BIC</p>
                <p className="font-medium text-slate-900 font-mono">{sepa.bic}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {order.notes && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle>Anmerkungen</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 whitespace-pre-wrap">{order.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrderDetail;
